import { useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router";
import { ChevronLeft } from "lucide-react";
import { useToast } from "../../components/ui/Toast";
import {
  ALL_AGREEMENT_TYPES,
  CREATE_AGREEMENT_TYPE,
  type AgreementTypeRecord,
} from "../../apollo/agreementTypes";
import { slugify } from "../../utils/slugify";

const CREATE_AGREEMENT = gql`
  mutation CreateAgreementInPortal(
    $name: String!
    $agreementType: String!
    $agreementTypeId: String
    $state: String
    $jurisdictionKind: String
    $jurisdictionValue: String
    $agreementHtml: String!
    $simpleHtml: String!
  ) {
    createAgreement(
      name: $name
      agreementType: $agreementType
      agreementTypeId: $agreementTypeId
      state: $state
      jurisdictionKind: $jurisdictionKind
      jurisdictionValue: $jurisdictionValue
      agreementHtml: $agreementHtml
      simpleHtml: $simpleHtml
    ) {
      id
      name
      agreementType
      agreementTypeId
      state
      jurisdictionKind
      jurisdictionValue
      isActive
    }
  }
`;

const JURISDICTION_VALUES = gql`
  query JurisdictionValuesForCreate($kind: String!, $state: String) {
    jurisdictionValues(kind: $kind, state: $state)
  }
`;

type JurisdictionKind = "national" | "state" | "county" | "city" | "zip";

const JURISDICTION_KINDS: { value: JurisdictionKind; label: string }[] = [
  { value: "national", label: "National" },
  { value: "state", label: "State" },
  { value: "county", label: "County" },
  { value: "city", label: "City" },
  { value: "zip", label: "ZIP code" },
];

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function titleCase(s: string): string {
  return s
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\B\w+/g, (w) => w.toLowerCase());
}

const STARTER_BODY = `<h2>Service Agreement</h2><p>Replace this placeholder with the full agreement text. Use the toolbar's <strong>Insert Variable</strong> menu to drop in booking-specific values like <code>{{customer_name_1}}</code>, <code>{{date}}</code>, and <code>{{booking_fee}}</code>.</p>`;

const STARTER_SIMPLE = `<p>By booking, <strong>{{customer_name_1}}</strong> agrees to a flat fee of <strong>{{booking_fee}}</strong> for the consultation booked on <strong>{{date}}</strong>.</p>`;

/** Check whether the jurisdiction fields are fully specified. */
function jurisdictionComplete(
  kind: JurisdictionKind,
  stateVal: string,
  valueVal: string,
): boolean {
  if (kind === "national") return true;
  if (!stateVal) return false; // state/county/city/zip all need state
  if (kind === "state") return true;
  return valueVal.length > 0; // county/city/zip need jurisdictionValue
}

const NEW_TYPE_SENTINEL = "__new__";

export function AgreementCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [isNewType, setIsNewType] = useState(false);

  // New agreement type inline create fields
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeKey, setNewTypeKey] = useState("");
  const [newTypeKeyEdited, setNewTypeKeyEdited] = useState(false);

  const [jurisdictionKind, setJurisdictionKind] =
    useState<JurisdictionKind>("national");
  const [state, setState] = useState("");
  const [jurisdictionValue, setJurisdictionValue] = useState("");
  const [create, { loading }] = useMutation<{
    createAgreement: { id: string; name: string };
  }>(CREATE_AGREEMENT);

  const [createAgreementType, { loading: creatingType }] = useMutation(CREATE_AGREEMENT_TYPE);

  // Load existing AgreementTypes for the picker
  const atQuery = useQuery<{ allAgreementTypes: AgreementTypeRecord[] }>(
    ALL_AGREEMENT_TYPES,
    {
      variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC", filter: { status: "active" } },
      fetchPolicy: "cache-and-network",
    },
  );

  const agreementTypes = atQuery.data?.allAgreementTypes ?? [];
  const typesLoaded = !atQuery.loading || !!atQuery.data;

  // When there are no existing types, default to new-type mode
  useEffect(() => {
    if (typesLoaded && agreementTypes.length === 0) {
      setIsNewType(true);
    }
  }, [typesLoaded, agreementTypes.length]);

  // Default to the first type once loaded (only if not in new mode)
  useEffect(() => {
    if (
      !isNewType &&
      !selectedTypeId &&
      agreementTypes.length > 0
    ) {
      setSelectedTypeId(agreementTypes[0].id);
    }
  }, [isNewType, selectedTypeId, agreementTypes]);

  function handleTypeSelectChange(value: string) {
    if (value === NEW_TYPE_SENTINEL) {
      setIsNewType(true);
      setSelectedTypeId("");
      setNewTypeName("");
      setNewTypeKey("");
      setNewTypeKeyEdited(false);
    } else {
      setIsNewType(false);
      setSelectedTypeId(value);
      setNewTypeName("");
      setNewTypeKey("");
      setNewTypeKeyEdited(false);
    }
  }

  // Find the selected AgreementType for dual-write
  const selectedType = useMemo(
    () => agreementTypes.find((t) => t.id === selectedTypeId),
    [agreementTypes, selectedTypeId],
  );

  // Jurisdiction value picker
  const [fetchJurisdictionValues, jurisdictionQuery] = useLazyQuery<{
    jurisdictionValues: string[];
  }>(JURISDICTION_VALUES);

  useEffect(() => {
    if (
      (jurisdictionKind === "county" ||
        jurisdictionKind === "city" ||
        jurisdictionKind === "zip") &&
      state
    ) {
      void fetchJurisdictionValues({
        variables: { kind: jurisdictionKind, state },
      });
    }
  }, [jurisdictionKind, state, fetchJurisdictionValues]);

  // Reset dependent values when kind changes
  function handleKindChange(next: JurisdictionKind) {
    setJurisdictionKind(next);
    setState("");
    setJurisdictionValue("");
  }

  function handleStateChange(next: string) {
    setState(next);
    setJurisdictionValue("");
  }

  // Fetch states for the state picker (used by state/county/city/zip kinds)
  const [fetchStates, statesQuery] = useLazyQuery<{
    jurisdictionValues: string[];
  }>(JURISDICTION_VALUES);

  useEffect(() => {
    if (
      jurisdictionKind === "state" ||
      jurisdictionKind === "county" ||
      jurisdictionKind === "city" ||
      jurisdictionKind === "zip"
    ) {
      void fetchStates({ variables: { kind: "state" } });
    }
  }, [jurisdictionKind, fetchStates]);

  const statesList = statesQuery.data?.jurisdictionValues ?? [];
  const valuesList = jurisdictionQuery.data?.jurisdictionValues ?? [];

  const effectiveTypeKey = isNewType
    ? (newTypeKey.trim() || slugify(newTypeName))
    : (selectedType?.typeKey ?? "");

  const canSubmit =
    name.trim().length > 0 &&
    // For an existing type, require it to be resolvable so dual-write never
    // sends an empty agreementType string (list still loading / type deleted).
    (isNewType
      ? newTypeName.trim().length > 0
      : selectedTypeId.length > 0 && selectedType != null) &&
    jurisdictionComplete(jurisdictionKind, state, jurisdictionValue) &&
    !loading &&
    !creatingType;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      let typeId = selectedTypeId;
      let typeKey = effectiveTypeKey;

      // If creating a new type, do that first
      if (isNewType) {
        const typeResult = await createAgreementType({
          variables: {
            name: newTypeName.trim(),
            typeKey: typeKey,
            isActive: true,
          },
          refetchQueries: [{ query: ALL_AGREEMENT_TYPES, variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC", filter: { status: "active" } } }],
        });
        const created = typeResult.data?.createAgreementType as AgreementTypeRecord | undefined;
        if (!created) {
          toast.push({ kind: "error", title: "Couldn't create agreement type" });
          return;
        }
        typeId = created.id;
        typeKey = created.typeKey;
      }

      // Dual-write: agreementTypeId + agreementType (legacy string = typeKey)
      const result = await create({
        variables: {
          name: name.trim(),
          agreementType: typeKey,
          agreementTypeId: typeId,
          state: state || null,
          jurisdictionKind,
          jurisdictionValue: jurisdictionValue || null,
          agreementHtml: STARTER_BODY,
          simpleHtml: STARTER_SIMPLE,
        },
      });
      const createdAgreement = result.data?.createAgreement;
      if (createdAgreement) {
        toast.push({
          kind: "success",
          title: `${createdAgreement.name} created`,
          description: "Edit content and document design, then save.",
        });
        navigate(`/settings/agreements/${createdAgreement.id}`);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't create agreement",
        description: message,
      });
    }
  }

  const needsState =
    jurisdictionKind === "state" ||
    jurisdictionKind === "county" ||
    jurisdictionKind === "city" ||
    jurisdictionKind === "zip";

  const needsValue =
    jurisdictionKind === "county" ||
    jurisdictionKind === "city" ||
    jurisdictionKind === "zip";

  return (
    <div className="max-w-xl">
      <div className="hair-b border-b sticky top-[var(--banner-h)] z-20 bg-paper/90 backdrop-blur">
        <div className="h-14 px-7 flex items-center gap-3">
          <Link
            to="/settings/agreements"
            className="text-ink-4 hover:text-ink-2 flex items-center gap-1.5 text-[12.5px]"
          >
            <ChevronLeft size={14} strokeWidth={2} />
            Agreements
          </Link>
          <span className="text-ink-5">&rsaquo;</span>
          <span className="text-ink font-medium text-[13.5px]">
            New agreement
          </span>
        </div>
      </div>

      <div className="px-7 py-8">
        <div className="mb-6">
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-1.5">
            New agreement
          </div>
          <h2 className="font-display text-[28px] font-medium tracking-tight">
            Start a new contract
          </h2>
          <p className="text-[12.5px] text-ink-3 mt-1.5">
            Pick a name, agreement type, and jurisdiction. We'll seed the body with a
            starter template — you'll fill in the actual content on the
            next page. The agreement starts as <span className="font-medium">inactive</span> until you flip the
            status toggle.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4"
        >
          <label className="block">
            <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Service Agreement -- Individual"
              autoFocus
              className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
            />
          </label>

          <div className="block">
            <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
              Agreement Type
            </span>
            {!typesLoaded ? (
              <div className="mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] text-ink-4">
                Loading agreement types...
              </div>
            ) : isNewType && agreementTypes.length === 0 ? (
              /* No existing types -- go straight to inline create */
              <div className="mt-1 space-y-2">
                <input
                  type="text"
                  value={newTypeName}
                  onChange={(e) => {
                    setNewTypeName(e.target.value);
                    if (!newTypeKeyEdited) setNewTypeKey(slugify(e.target.value));
                  }}
                  placeholder="e.g. Individual Fee Agreement"
                  className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
                <input
                  type="text"
                  value={newTypeKey}
                  onChange={(e) => {
                    setNewTypeKey(e.target.value);
                    setNewTypeKeyEdited(true);
                  }}
                  placeholder="individual-fee-agreement"
                  className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
                <span className="text-[10.5px] text-ink-5 block">
                  Type key (slug). Auto-suggested from name. Cannot be changed later.
                </span>
              </div>
            ) : isNewType ? (
              /* Inline new type creation with option to go back */
              <div className="mt-1 space-y-2">
                <input
                  type="text"
                  value={newTypeName}
                  onChange={(e) => {
                    setNewTypeName(e.target.value);
                    if (!newTypeKeyEdited) setNewTypeKey(slugify(e.target.value));
                  }}
                  placeholder="e.g. Individual Fee Agreement"
                  autoFocus
                  className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
                <input
                  type="text"
                  value={newTypeKey}
                  onChange={(e) => {
                    setNewTypeKey(e.target.value);
                    setNewTypeKeyEdited(true);
                  }}
                  placeholder="individual-fee-agreement"
                  className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
                <span className="text-[10.5px] text-ink-5 block">
                  Type key (slug). Auto-suggested from name. Cannot be changed later.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setIsNewType(false);
                    setNewTypeName("");
                    setNewTypeKey("");
                    setNewTypeKeyEdited(false);
                    if (agreementTypes.length > 0) {
                      setSelectedTypeId(agreementTypes[0].id);
                    }
                  }}
                  className="text-[11px] text-tide hover:text-tide-d font-medium"
                >
                  Pick an existing agreement type instead
                </button>
              </div>
            ) : (
              /* Normal select with existing agreement types + new option */
              <select
                value={selectedTypeId}
                onChange={(e) => handleTypeSelectChange(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              >
                {agreementTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
                <option value={NEW_TYPE_SENTINEL}>
                  + New agreement type...
                </option>
              </select>
            )}
            <span className="text-[10.5px] text-ink-5 mt-1 block">
              {isNewType
                ? "Create a new agreement type. The type key is the stable slug used for legacy compatibility."
                : "The document family this agreement version belongs to. Services link to agreement types, not individual versions."}
            </span>
          </div>

          {/* Jurisdiction picker */}
          <div>
            <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold block mb-1">
              Jurisdiction
            </span>
            <div className="space-y-3">
              <select
                value={jurisdictionKind}
                onChange={(e) =>
                  handleKindChange(e.target.value as JurisdictionKind)
                }
                className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              >
                {JURISDICTION_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>

              {needsState && (
                <select
                  value={state}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                >
                  <option value="">Select state...</option>
                  {statesList.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}

              {needsValue && state && (
                <select
                  value={jurisdictionValue}
                  onChange={(e) => setJurisdictionValue(e.target.value)}
                  className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                >
                  <option value="">
                    Select {humanize(jurisdictionKind).toLowerCase()}...
                  </option>
                  {valuesList.map((v) => (
                    <option key={v} value={v}>
                      {titleCase(v.toLowerCase())}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="hair-t border-t pt-4 flex items-center justify-end gap-2">
            <Link
              to="/settings/agreements"
              className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
            >
              {loading || creatingType ? "Creating..." : "Create & edit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
